// TES-1031 — field-level validation for the approval forms.
//
// The run-through: "Godkänn behandling" refused with a disabled button and
// one line of text, and it was not clear which field was wrong. The forms
// now keep the button pressable, and a press on an incomplete form marks
// every invalid field, scrolls to and focuses the first one, and lists them
// at the button.
//
// Pure on purpose (no React, and the DOM only behind a small interface) so
// `node --test` can run it.

/** One invalid field: its key, the label the summary names, the message shown at it. */
export interface FieldError {
  key: string;
  label: string;
  message: string;
}

/** A field the form can mark, in the order it appears on screen. */
export interface FieldSpec {
  key: string;
  label: string;
}

/**
 * The invalid fields in screen order. `messages` holds what each field says
 * (a missing or empty entry means the field is fine); fields the form does not
 * list are ignored, so the order is always the form's own.
 */
export function orderedFieldErrors(
  fields: readonly FieldSpec[],
  messages: Readonly<Record<string, string | null | undefined>>,
): FieldError[] {
  const out: FieldError[] = [];
  for (const f of fields) {
    const message = messages[f.key];
    if (message) out.push({ key: f.key, label: f.label, message });
  }
  return out;
}

/** The line at the submit button: how many fields and which. Null when none. */
export function errorSummary(errors: readonly FieldError[]): string | null {
  if (errors.length === 0) return null;
  return `Kontrollera ${errors.length} fält: ${errors.map((e) => e.label).join(", ")}.`;
}

/** One issue from a 400 response: a dotted path and the server's message. */
export interface ServerIssue {
  path: string;
  message: string;
}

/**
 * Server-side refusals mapped onto the form's own fields. `pathToKey` names the
 * field for a request path ("approval.dose" -> "dose"); the longest matching
 * prefix wins, so "webdocControl.labOrderDoneInWebdoc" can map through
 * "webdocControl". A field's local message is preferred where the form has one,
 * because the server's messages are partly machine codes
 * ("prescription_choice_required"); a Swedish server message is kept as is.
 * Issues on paths the form does not show are returned in `unmapped`.
 */
export function mapServerIssues(
  issues: readonly ServerIssue[],
  pathToKey: Readonly<Record<string, string>>,
  localMessage: (key: string) => string | null,
): { messages: Record<string, string>; unmapped: ServerIssue[] } {
  const messages: Record<string, string> = {};
  const unmapped: ServerIssue[] = [];
  const prefixes = Object.keys(pathToKey).sort((a, b) => b.length - a.length);
  for (const issue of issues) {
    const prefix = prefixes.find(
      (p) => issue.path === p || issue.path.startsWith(`${p}.`),
    );
    if (!prefix) {
      unmapped.push(issue);
      continue;
    }
    const key = pathToKey[prefix]!;
    if (messages[key]) continue;
    const isCode = /^[a-z0-9_.]+$/.test(issue.message);
    messages[key] =
      (isCode ? localMessage(key) : issue.message) ??
      localMessage(key) ??
      "Kontrollera fältet.";
  }
  return { messages, unmapped };
}

/** The part of an element that focusing needs. */
export interface Focusable {
  scrollIntoView(options?: ScrollIntoViewOptions): void;
  focus(options?: FocusOptions): void;
}

/**
 * Scroll to and focus the first invalid field. `find` resolves a field key to
 * the element to focus (the input itself, or the first control inside the
 * field's group). Fields that cannot be found (not rendered) are skipped.
 * Returns the key that got focus, or null.
 */
export function focusFirstInvalid(
  errors: readonly FieldError[],
  find: (key: string) => Focusable | null,
): string | null {
  for (const e of errors) {
    const el = find(e.key);
    if (!el) continue;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.focus({ preventScroll: true });
    return e.key;
  }
  return null;
}

/** The DOM id of a field's wrapper, so a form's fields are found by key. */
export function fieldDomId(form: string, key: string): string {
  return `${form}-field-${key}`;
}

/**
 * The element to focus for a field: the wrapper itself when it is a control,
 * else its first enabled control, else the wrapper.
 */
export function findFieldElement(
  doc: Pick<Document, "getElementById">,
  form: string,
  key: string,
): HTMLElement | null {
  const wrap = doc.getElementById(fieldDomId(form, key));
  if (!wrap) return null;
  if (/^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(wrap.tagName)) return wrap;
  return (
    wrap.querySelector<HTMLElement>(
      "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])",
    ) ?? wrap
  );
}
